import type { ReactNode } from 'react';

import type { PitchAlgorithm } from '@muzly/shared';

import { InfoTip } from './InfoTip';
import {
  DEFAULT_CAPTURE_RATE,
  useSessionControl,
} from './sessionControlContext';
import { type SessionState, formatSinceFrame } from './sessionReconcile';

import type { BrowserSource, MicFailure } from '../../../audio';

/**
 * 세션 제어 패널 — **상태값만** 보여준다(#25, #27). 모든 transport 제어(시작/정지/
 * 일시정지/재개)는 전역 sticky `SessionTransport` 바 하나로 통일됐다(중복 제거).
 *
 * 이 카드가 남기는 것: 알고리즘 선택(idle 한정)·세션 상태·captureRate·fps.
 * 탭 콘솔(#27)에선 '튜닝' 탭에 들어가고, transport는 어느 탭에서든 sticky 바로 닿는다.
 *
 * 상태/세션 구동은 `SessionControlProvider`(컨텍스트)가 `usePitchSession`으로
 * 소유하고, 이 패널은 그 표면을 소비만 한다.
 */

const STATE_LABEL: Record<SessionState, string> = {
  idle: '대기',
  running: '실행 중',
  paused: '일시정지',
};

const ALGORITHMS: PitchAlgorithm[] = ['mpm', 'yin'];

interface AlgorithmSegmentProps {
  value: PitchAlgorithm;
  onChange: (algorithm: PitchAlgorithm) => void;
  /** running/paused 중에는 비활성(정지 후에만 변경 가능). */
  disabled: boolean;
}

const AlgorithmSegment = ({
  value,
  onChange,
  disabled,
}: AlgorithmSegmentProps) => (
  <div
    className={`inline-flex rounded-lg border border-border/60 p-0.5 ${
      disabled ? 'opacity-50' : ''
    }`}
  >
    {ALGORITHMS.map((algo) => (
      <button
        key={algo}
        type="button"
        disabled={disabled}
        onClick={() => onChange(algo)}
        className={`min-h-[44px] rounded-md px-4 text-xs font-medium uppercase transition disabled:cursor-not-allowed motion-safe:enabled:active:scale-95 ${
          value === algo
            ? 'bg-primary text-primary-foreground'
            : 'text-muted-foreground enabled:hover:bg-muted'
        }`}
      >
        {algo}
      </button>
    ))}
  </div>
);

const SOURCES: { value: BrowserSource; label: string }[] = [
  { value: 'mock', label: '데모' },
  { value: 'mic', label: '내 목소리' },
];

interface SourceSegmentProps {
  value: BrowserSource;
  onChange: (source: BrowserSource) => void;
  disabled: boolean;
  micError: MicFailure | null;
}

/**
 * 브라우저 전용 프레임 소스 선택. 네이티브에서는 렌더하지 않는다(항상 실제 엔진).
 *
 * 왜 필요한가: 데모 프레임은 confidence 0.95 · rms −18dBFS · 고정 음높이라 **어떤
 * 임계값에도 걸리지 않는다** → 슬라이더를 굴려도 화면이 그대로다. 실제 목소리가 들어와야
 * 튜닝이 눈에 보인다. 대신 브라우저의 추정기는 JS ACF이고 네이티브 MPM이 아니다.
 */
const SourceSegment = ({
  value,
  onChange,
  disabled,
  micError,
}: SourceSegmentProps) => (
  <div className="flex flex-col gap-1">
    <div className="flex items-center gap-1.5">
      <div
        className={`inline-flex rounded-lg border border-border/60 p-0.5 ${
          disabled ? 'opacity-50' : ''
        }`}
      >
        {SOURCES.map((option) => (
          <button
            key={option.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(option.value)}
            className={`min-h-[44px] rounded-md px-4 text-xs font-medium transition disabled:cursor-not-allowed motion-safe:enabled:active:scale-95 ${
              value === option.value
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground enabled:hover:bg-muted'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
      <InfoTip label="프레임 소스 설명">
        “데모”는 가짜 음정을 흘리는 고정 스트림이라 설정을 바꿔도 화면이 변하지
        않습니다. “내 목소리”는 마이크를 열어 실제 소리를 분석하므로
        무음·음역·신뢰도 임계값이 실제로 작동합니다. 단 브라우저의 추정기는
        ACF이고 실기기의 MPM 엔진과 다른 알고리즘입니다 — 값 자체를 실기기와
        비교하지 마세요.
      </InfoTip>
    </div>
    {micError && (
      <span className="text-[11px] text-red-600">
        {micError === 'denied'
          ? '마이크 권한이 거부됐습니다. 주소창 권한 설정에서 허용 후 다시 시작하세요.'
          : '이 브라우저는 마이크 캡처를 지원하지 않습니다.'}
      </span>
    )}
  </div>
);

interface StatCellProps {
  label: string;
  tip: string;
  value: ReactNode;
  /** stale/무입력 등 주의가 필요한 값은 빨강으로 강조. */
  alert?: boolean;
}

const StatCell = ({ label, tip, value, alert = false }: StatCellProps) => (
  <div className="flex flex-col gap-0.5 rounded-md border border-border bg-card p-2">
    <span className="flex items-center gap-1 text-xs text-muted-foreground">
      {label}
      <InfoTip label={`${label} 설명`}>{tip}</InfoTip>
    </span>
    <span
      className={`font-mono text-sm ${
        alert ? 'text-destructive' : 'text-foreground'
      }`}
    >
      {value}
    </span>
  </div>
);

export const SessionControlPanel = () => {
  const {
    isNative,
    algorithm,
    setAlgorithm,
    sessionState,
    captureSampleRate,
    fps,
    liveness,
    can,
    source,
    setSource,
    micError,
  } = useSessionControl();

  return (
    <div className="flex flex-col gap-3">
      {/* 브라우저에서만: 프레임을 데모로 받을지 실제 마이크로 받을지. */}
      {!isNative && (
        <SourceSegment
          value={source}
          onChange={setSource}
          disabled={!can.source}
          micError={micError}
        />
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <AlgorithmSegment
            value={algorithm}
            onChange={setAlgorithm}
            disabled={!can.algorithm || (!isNative && source === 'mic')}
          />
          <InfoTip label="알고리즘 설명">
            MPM은 기본·빠른 추정, YIN은 비교용 정밀 추정입니다. 바꾼 값은 정지
            후 다시 시작해야 적용됩니다. 브라우저 마이크 소스는 이 선택과
            무관하게 항상 ACF로 분석합니다 — 네이티브 엔진이 브라우저에 없기
            때문입니다.
          </InfoTip>
        </div>
        <span className="text-xs text-muted-foreground">
          {isNative
            ? 'native'
            : source === 'mic'
              ? 'browser mic · ACF'
              : 'mock'}{' '}
          · {can.algorithm ? '선택은 다음 시작에 적용' : '정지 후 변경 가능'}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <StatCell
          label="상태"
          tip="세션 상태입니다. 대기/실행 중/일시정지. 프레임이 끊기면 '무입력'으로 표시됩니다."
          value={liveness.stalled ? '무입력 (끊김)' : STATE_LABEL[sessionState]}
          alert={liveness.stalled}
        />
        <StatCell
          label="마지막 프레임"
          tip="마지막 분석 프레임이 들어온 지 얼마나 지났는지입니다. 길어지면 입력이 끊긴 것입니다."
          value={formatSinceFrame(liveness.sinceLastFrameMs)}
        />
        <StatCell
          label="captureRate"
          tip="마이크 오디오를 초당 몇 번 샘플링하는지(Hz)입니다. 보통 48000Hz."
          value={`${captureSampleRate ?? DEFAULT_CAPTURE_RATE} Hz`}
        />
        <StatCell
          label="fps"
          tip="초당 들어오는 분석 프레임 수입니다. 음정이 얼마나 자주 갱신되는지를 뜻합니다."
          value={fps}
        />
      </div>
    </div>
  );
};
